/**
 * SmartKargo 20th anniversary — official emblem (paths copied unchanged from the marketing kit,
 * smartkargo-20-years-horizontal-reversed.svg). It is used on its own (the SmartKargo logo is already
 * on every screen) in its reversed colours, blended into the Hub's own dark surfaces at the IT Team's
 * request instead of on a navy panel: the ring's centre is left open so the surface shows through, and
 * the flight path's separating gap takes the surface colour (`surface`). The mark's shapes and colours
 * and the approved wording are unchanged; clear space of at least the height of "YEARS" is kept.
 * Motion: one entrance that assembles the mark and ends on the official artwork; replays on hover;
 * static under reduced motion. Retire after the anniversary year: remove <AnniversaryBanner />
 * (AppShell) and <AnniversaryFeature /> (LoginPage).
 */
import { useState } from 'react';
import { Plane } from 'lucide-react';

export const SK_ORANGE = '#D94413';
export const SK_NAVY = '#201446';

const TWO =
  'm 10.584,166 v -12.936 q 17.64,-13.916 31.164,-25.088 13.524,-11.368 22.736,-20.972 9.212,-9.8 13.916,-18.228 4.704,-8.624 4.704,-17.052 0,-6.272 -2.352,-10.78 -2.352,-4.508 -6.272,-7.448 -3.724,-2.94 -8.624,-4.312 -4.9,-1.372 -9.996,-1.372 -11.76,0 -20.776,4.508 -9.016,4.508 -14.7,11.956 L 10.388,53.888 Q 17.836,43.892 29.792,38.6 41.944,33.308 55.86,33.308 q 8.232,0 16.072,2.352 7.84,2.352 13.916,7.056 6.272,4.704 9.996,11.956 3.92,7.252 3.92,17.052 0,9.996 -4.9,19.796 -4.704,9.604 -13.328,19.404 -8.428,9.604 -19.992,19.796 -11.564,9.996 -25.284,20.776 h 64.092 V 166 Z';
const RING = 'M 250.5,94 A 69.5,69.5 0 0 1 181,163.5 69.5,69.5 0 0 1 111.5,94 69.5,69.5 0 0 1 181,24.5 69.5,69.5 0 0 1 250.5,94 Z';
const CONTINENTS = [
  'm 165,48 c 9,-11 26,-17 42,-15 l 11,7 -7,10 -13,-1 -6,9 -12,3 -6,13 -12,-2 -7,-11 z',
  'm 224,44 17,7 11,12 -5,9 -10,-3 -5,10 -12,-2 -4,-12 z',
  'm 151,88 15,-9 17,4 9,11 -5,12 9,9 -7,22 -12,8 -8,-17 -12,-8 -9,-16 z',
  'm 221,94 17,-8 15,5 5,14 -9,10 -7,20 -13,8 -5,-17 -9,-12 z',
];
const TRAIL = 'm 108,144 c 42,17 105,9 147,-18 31,-20 53,-54 71,-94';
const PLANE = 'm 0,-29 c 4,4 5,11 5,19 l 1,7 17,14 -1,5 -16,-7 -1,13 6,6 -4,3 -7,-4 -7,4 -4,-3 6,-6 -1,-13 -16,7 -1,-5 17,-14 1,-7 c 0,-8 1,-15 5,-19 z';
const YEARS = [
  'm 389.436,117 h -10.908 v -14.31 l -13.5,-21.708 h 12.258 l 6.696,12.15 6.588,-12.15 h 12.258 l -13.392,21.708 z',
  'm 439.09598,117 h -27.324 V 80.982 h 27.324 v 9.504 h -16.416 v 3.564 h 16.038 v 9.504 h -16.038 v 3.942 h 16.416 z',
  'm 487.35201,117 h -12.366 l -1.296,-4.212 h -12.96 l -1.296,4.212 h -12.366 l 13.284,-36.018 h 13.716 z m -16.578,-13.716 -3.564,-11.61 -3.564,11.61 z',
  'm 528.53396,117 h -12.42 l -4.968,-11.34 h -3.942 V 117 h -10.908 V 80.982 h 19.224 q 6.102,0 9.504,3.564 3.456,3.51 3.456,8.802 0,4.212 -1.944,6.912 -1.89,2.7 -4.536,3.996 z m -14.688,-20.844 q 1.512,0 2.538,-0.702 1.026,-0.756 1.026,-2.16 0,-1.35 -1.026,-2.052 -1.026,-0.756 -2.538,-0.756 h -6.642 v 5.67 z',
  'm 553.89394,117.648 q -10.368,0 -16.74,-5.724 l 5.67,-8.1 q 4.86,4.644 11.61,4.644 3.996,0 3.996,-1.944 0,-0.918 -2.106,-1.404 -2.052,-0.486 -5.022,-1.026 -2.916,-0.54 -5.886,-1.566 -2.916,-1.026 -5.022,-3.618 -2.052,-2.592 -2.052,-6.534 0,-4.914 3.942,-8.424 3.996,-3.51 11.016,-3.51 9.126,0 15.282,5.022 l -5.832,7.722 q -4.698,-3.564 -10.584,-3.564 -2.754,0 -2.754,1.62 0,0.756 1.458,1.296 1.512,0.54 3.726,0.864 2.214,0.27 4.806,1.08 2.646,0.756 4.86,1.836 2.214,1.08 3.672,3.402 1.512,2.268 1.512,5.4 0,5.94 -4.158,9.234 -4.104,3.294 -11.394,3.294 z',
];
const DATES = [
  'm 384.822,160 h -14.715 v -4.239 q 3.375,-2.376 4.941,-3.51 1.566,-1.134 2.619,-2.052 1.053,-0.918 1.242,-1.323 0.216,-0.405 0.216,-0.945 0,-0.621 -0.594,-0.999 -0.594,-0.405 -1.485,-0.405 -2.538,0 -4.752,1.863 l -2.97,-3.618 q 1.458,-1.566 3.483,-2.295 2.052,-0.756 4.131,-0.756 3.483,0 5.589,1.755 2.133,1.728 2.133,4.455 0,1.89 -1.269,3.564 -1.269,1.674 -4.428,3.753 h 5.859 z',
  'm 401.95702,159.001 q -1.836,1.323 -4.428,1.323 -2.592,0 -4.455,-1.323 -1.836,-1.35 -2.7,-3.402 -0.837,-2.079 -0.837,-4.59 0,-3.915 2.079,-6.588 2.079,-2.7 5.913,-2.7 3.834,0 5.913,2.7 2.079,2.673 2.079,6.588 0,2.511 -0.864,4.59 -0.837,2.052 -2.7,3.402 z m -6.939,-7.992 q 0,4.509 2.511,4.509 2.511,0 2.511,-4.509 0,-4.482 -2.511,-4.482 -2.511,0 -2.511,4.482 z',
  'm 422.41303,159.001 q -1.836,1.323 -4.428,1.323 -2.592,0 -4.455,-1.323 -1.836,-1.35 -2.7,-3.402 -0.837,-2.079 -0.837,-4.59 0,-3.915 2.079,-6.588 2.079,-2.7 5.913,-2.7 3.834,0 5.913,2.7 2.079,2.673 2.079,6.588 0,2.511 -0.864,4.59 -0.837,2.052 -2.7,3.402 z m -6.939,-7.992 q 0,4.509 2.511,4.509 2.511,0 2.511,-4.509 0,-4.482 -2.511,-4.482 -2.511,0 -2.511,4.482 z',
  'm 439.03504,160.324 q -4.239,0 -6.426,-2.565 -2.16,-2.565 -2.16,-6.723 0,-4.428 2.565,-6.858 2.592,-2.457 6.885,-2.457 3.321,0 5.616,1.566 l -2.295,4.212 q -1.377,-0.972 -3.321,-0.972 -1.512,0 -2.727,0.81 -1.215,0.81 -1.215,2.322 v 0.054 q 1.431,-1.836 4.185,-1.836 2.592,0 4.482,1.62 1.89,1.593 1.89,4.347 0,2.889 -2.133,4.698 -2.106,1.782 -5.346,1.782 z m -0.351,-4.806 q 0.999,0 1.647,-0.432 0.648,-0.432 0.648,-1.026 0,-0.783 -0.783,-1.161 -0.756,-0.405 -1.728,-0.405 -1.593,0 -2.538,0.729 0.081,0.999 0.756,1.647 0.675,0.648 1.998,0.648 z',
  'm 465.53904,155.491 h -14.391 v -4.05 h 14.391 z',
  'm 485.67104,160 h -14.715 v -4.239 q 3.375,-2.376 4.941,-3.51 1.566,-1.134 2.619,-2.052 1.053,-0.918 1.242,-1.323 0.216,-0.405 0.216,-0.945 0,-0.621 -0.594,-0.999 -0.594,-0.405 -1.485,-0.405 -2.538,0 -4.752,1.863 l -2.97,-3.618 q 1.458,-1.566 3.483,-2.295 2.052,-0.756 4.131,-0.756 3.483,0 5.589,1.755 2.133,1.728 2.133,4.455 0,1.89 -1.269,3.564 -1.269,1.674 -4.428,3.753 h 5.859 z',
  'm 502.80607,159.001 q -1.836,1.323 -4.428,1.323 -2.592,0 -4.455,-1.323 -1.836,-1.35 -2.7,-3.402 -0.837,-2.079 -0.837,-4.59 0,-3.915 2.079,-6.588 2.079,-2.7 5.913,-2.7 3.834,0 5.913,2.7 2.079,2.673 2.079,6.588 0,2.511 -0.864,4.59 -0.837,2.052 -2.7,3.402 z m -6.939,-7.992 q 0,4.509 2.511,4.509 2.511,0 2.511,-4.509 0,-4.482 -2.511,-4.482 -2.511,0 -2.511,4.482 z',
  'm 526.17808,160 h -14.715 v -4.239 q 3.375,-2.376 4.941,-3.51 1.566,-1.134 2.619,-2.052 1.053,-0.918 1.242,-1.323 0.216,-0.405 0.216,-0.945 0,-0.621 -0.594,-0.999 -0.594,-0.405 -1.485,-0.405 -2.538,0 -4.752,1.863 l -2.97,-3.618 q 1.458,-1.566 3.483,-2.295 2.052,-0.756 4.131,-0.756 3.483,0 5.589,1.755 2.133,1.728 2.133,4.455 0,1.89 -1.269,3.564 -1.269,1.674 -4.428,3.753 h 5.859 z',
  'm 539.4791,160.324 q -4.239,0 -6.426,-2.565 -2.16,-2.565 -2.16,-6.723 0,-4.428 2.565,-6.858 2.592,-2.457 6.885,-2.457 3.321,0 5.616,1.566 l -2.295,4.212 q -1.377,-0.972 -3.321,-0.972 -1.512,0 -2.727,0.81 -1.215,0.81 -1.215,2.322 v 0.054 q 1.431,-1.836 4.185,-1.836 2.592,0 4.482,1.62 1.89,1.593 1.89,4.347 0,2.889 -2.133,4.698 -2.106,1.782 -5.346,1.782 z m -0.351,-4.806 q 0.999,0 1.647,-0.432 0.648,-0.432 0.648,-1.026 0,-0.783 -0.783,-1.161 -0.756,-0.405 -1.728,-0.405 -1.593,0 -2.538,0.729 0.081,0.999 0.756,1.647 0.675,0.648 1.998,0.648 z',
];

/**
 * The official 20 YEARS emblem (2006–2026). `reversed` is the dark-background version and must sit on
 * SmartKargo Navy. Each part carries a class so the entrance can assemble it; the final frame is the
 * unmodified official artwork.
 */
export function AnniversaryEmblem({ width = 240, surface = SK_NAVY, className = '' }: { width?: number; surface?: string; className?: string }) {
  const ink = '#ffffff';
  const ground = surface;
  return (
    <svg viewBox="0 0 590 185" width={width} height={Math.round((width * 185) / 590)} className={`sk20 shrink-0 ${className}`} role="img" aria-label="SmartKargo 20 Years, 2006–2026">
      <path className="sk20-two" d={TWO} fill={ink} />
      <path className="sk20-ring" d={RING} pathLength={1} style={{ fill: 'none', stroke: SK_ORANGE, strokeWidth: 26 }} />
      <g transform="matrix(0.77,0,0,0.77,22,24)" fill={ink} opacity={0.96}>
        {CONTINENTS.map((d, i) => (
          <path key={i} className="sk20-land" style={{ animationDelay: `${520 + i * 70}ms` }} d={d} />
        ))}
      </g>
      <path className="sk20-trail-under" d={TRAIL} pathLength={1} fill="none" stroke={ground} strokeWidth={20} strokeLinecap="round" />
      <path className="sk20-trail" d={TRAIL} pathLength={1} fill="none" stroke={SK_ORANGE} strokeWidth={8} strokeLinecap="round" />
      <g transform="matrix(0.86010965,0.60225526,-0.60225526,0.86010965,326,34)">
        <g className="sk20-plane" fill={SK_ORANGE}>
          <path d={PLANE} />
        </g>
      </g>
      <g className="sk20-years" fill={ink}>
        {YEARS.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
      <g className="sk20-dates" fill={ink}>
        {DATES.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
    </svg>
  );
}

/**
 * Sign-in feature: the emblem with the approved line beside it, directly on the dark stage (no panel).
 * Hovering replays the entrance.
 */
export function AnniversaryFeature({ className = '' }: { className?: string }) {
  const [run, setRun] = useState(0);
  return (
    <div className={`flex flex-wrap items-center gap-x-7 gap-y-3 ${className}`} onMouseEnter={() => setRun((n) => n + 1)}>
      <AnniversaryEmblem key={run} width={236} surface="#0d0f13" />
      <div className="sk20-line border-l border-white/12 pl-6">
        <p className="text-[10px] font-semibold tracking-[0.24em] text-[#ff8a57] uppercase">20th anniversary</p>
        <p className="mt-1.5 text-[19px] leading-snug font-semibold tracking-[-0.015em] text-white">
          Built by People.
          <br />
          Moving the World.
        </p>
      </div>
    </div>
  );
}

/** Portal strip above every page during the anniversary year: the Hub's graphite-to-ember surface. */
export function AnniversaryBanner() {
  const [run, setRun] = useState(0);
  return (
    <section aria-label="SmartKargo 20 Years" className="anniv-banner anniv-in relative isolate mb-6 overflow-hidden rounded-[16px] text-white" onMouseEnter={() => setRun((n) => n + 1)}>
      <div className="flex items-center gap-6 px-6 py-4">
        <AnniversaryEmblem key={run} width={176} surface="#171310" />
        <span aria-hidden className="sk20-rule hidden h-10 w-px shrink-0 bg-white/15 sm:block" />
        <div className="sk20-line hidden min-w-0 sm:block">
          <p className="text-[10px] font-semibold tracking-[0.22em] text-[#ff8a57] uppercase">20th anniversary</p>
          <p className="mt-0.5 text-[16px] leading-snug font-semibold tracking-[-0.01em]">Built by People. Moving the World.</p>
        </div>
        <AnniversaryTimeline key={`t${run}`} />
      </div>
    </section>
  );
}

const MILESTONES = [2006, 2011, 2016, 2021, 2026];

/**
 * Right side of the banner: the two decades as a flight path. The track draws from 2006 to 2026, the plane
 * flies along it and each five-year marker lights up as it passes; it ends on "2026 · 20 years".
 * Brand content only (no organisation data), so it is the same for every role. Decorative: the banner's
 * label already names the anniversary.
 */
function AnniversaryTimeline() {
  return (
    <div aria-hidden className="sk20-tl ml-auto hidden w-[min(46%,480px)] shrink-0 pr-2 lg:block">
      <div className="flex items-baseline justify-between text-[10px] font-semibold tracking-[0.2em] text-white/45 uppercase">
        <span>Since 2006</span>
        <span className="text-[#ff8a57]">Two decades</span>
      </div>
      <div className="relative mt-3 h-6">
        {/* base track */}
        <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[repeating-linear-gradient(90deg,rgb(255_255_255/0.22)_0_6px,transparent_6px_11px)]" />
        {/* flown path + plane travel together; the clip keeps them inside the track */}
        <div className="absolute inset-0 [overflow-x:clip]">
          <div className="sk20-flight absolute inset-0">
            <span className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-gradient-to-r from-[#d94413]/0 via-[#d94413] to-[#ff8a57] shadow-[0_0_12px_rgb(255_138_87/0.55)]" />
            <Plane className="sk20-plane-tl absolute top-1/2 right-0 size-4 translate-x-1/2 -translate-y-1/2 rotate-45 text-[#ffb38f] drop-shadow-[0_0_6px_rgb(255_138_87/0.8)]" strokeWidth={2} />
          </div>
        </div>
        {MILESTONES.map((y, i) => (
          <span
            key={y}
            className={`sk20-dot absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${i === MILESTONES.length - 1 ? 'sk20-dot-now size-2.5' : 'size-1.5'}`}
            style={{ left: `${(i / (MILESTONES.length - 1)) * 100}%`, ['--d' as string]: `${1700 + i * 260}ms` }}
          />
        ))}
      </div>
      <div className="relative mt-1.5 h-4 text-[11px] font-medium tnum">
        {MILESTONES.map((y, i) => (
          <span
            key={y}
            className={`sk20-year absolute whitespace-nowrap ${i === 0 ? '' : i === MILESTONES.length - 1 ? '-translate-x-full font-semibold text-white' : '-translate-x-1/2 text-white/50'} ${i === 0 ? 'text-white/50' : ''}`}
            style={{ left: `${(i / (MILESTONES.length - 1)) * 100}%`, ['--d' as string]: `${1700 + i * 260}ms` }}
          >
            {i === MILESTONES.length - 1 ? '2026 · 20 years' : y}
          </span>
        ))}
      </div>
    </div>
  );
}
